import { BUYER_DOC_TYPES, SELLER_DOC_TYPES } from "@/config/constants";

export const isValidFile = (file: File) => !!file && file instanceof File;

const initialFileDetails = {
  file: undefined,
  fileName: "",
  downloadUrl: "",
};

const sellerDocumentsUploadInitialFormState = {
  values: {
    hasEid: false,
    isUnderMortgage: true,
    passport: initialFileDetails,
    titleDeed: initialFileDetails,
    lastaddcBill: initialFileDetails,
    districtCoolingClearance: initialFileDetails,
    gasProvider: initialFileDetails,
    hassantukCert: initialFileDetails,
    mortgageLetter: initialFileDetails,
    emiratesIdFront: initialFileDetails,
    emiratesIdBack: initialFileDetails,
    mou: initialFileDetails,
    serviceChargeAssignment: initialFileDetails,
    provisNoc: initialFileDetails,
  },
  loading: {
    passport: false,
    titleDeed: false,
    lastaddcBill: false,
    districtCoolingClearance: false,
    gasProvider: false,
    hassantukCert: false,
    mortgageLetter: false,
    emiratesIdFront: false,
    emiratesIdBack: false,
    mou: undefined,
    serviceChargeAssignment: undefined,
    provisNoc: undefined,
  },
  errors: {
    passport: false,
    titleDeed: false,
    lastaddcBill: false,
    districtCoolingClearance: false,
    gasProvider: false,
    hassantukCert: false,
    mortgageLetter: false,
    emiratesIdFront: false,
    emiratesIdBack: false,
    mou: false,
    serviceChargeAssignment: false,
    provisNoc: false,
  },
  docuSignStatus: {
    serviceChargeAssignment: { status: false, signTime: "" },
  },
};

const buyerDocumentsUploadInitialFormState = {
  values: {
    passport: initialFileDetails,
    emiratesIdFront: initialFileDetails,
    emiratesIdBack: initialFileDetails,
  },
  loading: {
    passport: false,
    emiratesIdFront: false,
    emiratesIdBack: false,
  },
  errors: {
    passport: false,
    emiratesIdFront: false,
    emiratesIdBack: false,
  },
};

const SELLER_RELEVANT_DOCS_YES_FLOW = [
  SELLER_DOC_TYPES.EMIRATES_ID_FRONT,
  SELLER_DOC_TYPES.EMIRATES_ID_BACK,
  SELLER_DOC_TYPES.PASSPORT,
  SELLER_DOC_TYPES.PROVIS_NOC,
  SELLER_DOC_TYPES.MORTGAGE_LETTER,
];

const SELLER_RELEVANT_DOCS_NO_FLOW = [
  SELLER_DOC_TYPES.EMIRATES_ID_FRONT,
  SELLER_DOC_TYPES.EMIRATES_ID_BACK,
  SELLER_DOC_TYPES.PASSPORT,
  SELLER_DOC_TYPES.TITLE_DEED,
  SELLER_DOC_TYPES.MOU,
  SELLER_DOC_TYPES.ADDC,
  SELLER_DOC_TYPES.DISTRICT_COOLING_CLEARANCE,
  SELLER_DOC_TYPES.GAS_NOC,
  SELLER_DOC_TYPES.HASSANTUK_CERTIFICATE,
  SELLER_DOC_TYPES.MORTGAGE_LETTER,
];

const SELLER_DOC_TYPES_REQUIRING_EXPIRY_CHECK = [
  SELLER_DOC_TYPES.EMIRATES_ID_FRONT,
  SELLER_DOC_TYPES.EMIRATES_ID_BACK,
  SELLER_DOC_TYPES.PASSPORT,
];

const BUYER_RELEVANT_DOCS = [
  BUYER_DOC_TYPES.BUYER_EMIRATES_ID,
  BUYER_DOC_TYPES.BUYER_EMIRATES_ID_BACK,
  BUYER_DOC_TYPES.BUYER_PASSPORT,
];

const SELLER_DOCS_CONFIG: Record<
  string,
  {
    fieldName?: string;
    fieldNames?: string[];
    reduxKey: string;
  }
> = {
  [SELLER_DOC_TYPES.EMIRATES_ID_FRONT]: {
    fieldName: "emiratesIdFront",
    reduxKey: "emiratesIdFront",
  },
  [SELLER_DOC_TYPES.EMIRATES_ID_BACK]: {
    fieldName: "emiratesIdBack",
    reduxKey: "emiratesIdBack",
  },
  [SELLER_DOC_TYPES.PASSPORT]: {
    fieldName: "passport",
    reduxKey: "passport",
  },
  [SELLER_DOC_TYPES.TITLE_DEED]: {
    fieldName: "titleDeed",
    reduxKey: "titleDeed",
  },
  [SELLER_DOC_TYPES.PROVIS_NOC]: {
    fieldName: "provisNoc",
    reduxKey: "provisNoc",
  },
  [SELLER_DOC_TYPES.MOU]: {
    fieldName: "mou",
    reduxKey: "mou",
  },
  [SELLER_DOC_TYPES.ADDC]: {
    fieldName: "lastaddcBill",
    reduxKey: "nocForAddc",
  },
  [SELLER_DOC_TYPES.DISTRICT_COOLING_CLEARANCE]: {
    fieldName: "districtCoolingClearance",
    reduxKey: "districtCoolingTransfer",
  },
  [SELLER_DOC_TYPES.GAS_NOC]: {
    fieldName: "gasProvider",
    reduxKey: "gasClearanceCertificate",
  },
  [SELLER_DOC_TYPES.HASSANTUK_CERTIFICATE]: {
    fieldName: "hassantukCert",
    reduxKey: "hassantukCertificate",
  },
  [SELLER_DOC_TYPES.MORTGAGE_LETTER]: {
    fieldName: "mortgageLetter",
    reduxKey: "mortgageReleaseLetter",
  },
  [SELLER_DOC_TYPES.SUPPORTING_DOCS]: {
    reduxKey: "miscDocuments",
  },
};

const BUYER_DOCS_CONFIG: Record<
  string,
  {
    fieldName?: string;
    fieldNames?: string;
    fieldKey: string;
  }
> = {
  [BUYER_DOC_TYPES.BUYER_EMIRATES_ID]: {
    fieldName: "emiratesIdFront",
    fieldKey: "Buyer Emirates ID Copy",
  },
  [BUYER_DOC_TYPES.BUYER_EMIRATES_ID_BACK]: {
    fieldName: "emiratesIdBack",
    fieldKey: "Buyer Emirates ID BackSideCopy",
  },
  [BUYER_DOC_TYPES.BUYER_PASSPORT]: {
    fieldName: "passport",
    fieldKey: "Buyer Passport Copy",
  },
};

const validateFieldData = (fieldData: any) => {
  return isValidFile(fieldData?.file) || !!fieldData?.fileData;
};

export {
  sellerDocumentsUploadInitialFormState,
  SELLER_RELEVANT_DOCS_YES_FLOW,
  SELLER_RELEVANT_DOCS_NO_FLOW,
  SELLER_DOCS_CONFIG,
  validateFieldData,
  buyerDocumentsUploadInitialFormState,
  BUYER_RELEVANT_DOCS,
  BUYER_DOCS_CONFIG,
  SELLER_DOC_TYPES_REQUIRING_EXPIRY_CHECK
};
